// Custo unitário composto (2026-09-15): "Estação hidrológica" = painel + bateria +
// terminal ST6100… O servidor é quem grava a soma (comercial-engine,
// handlers/composicoes.js); esta régua só mostra a PRÉVIA na tela — e por isso
// precisa bater com o Postgres ao centavo: round(Σ quantidade × valor, 2), com o
// meio centavo arredondado para longe do zero.
//
// 🚨 Não use toFixed nem Math.round sobre o produto em ponto flutuante:
// (1.5 * 33.33).toFixed(2) dá "49.99" e o Postgres grava 50,00. A conta aqui é
// feita em inteiros (quantidade × 10⁴, valor × 10²).

export interface PecaParaSomar {
  quantidade: number | string;
  valor_unitario: number | string;
}

export interface CustoDoCatalogo {
  id: string;
  descricao: string;
  codigo?: string | null;
  tag?: string | null;
  categoria?: string | null;
  unidade?: string | null;
  valor_unitario: number | string;
  ativo?: boolean;
  composto?: boolean;
  componentes?: { componente_id: string }[] | null;
}

function numero(v: number | string | null | undefined): number {
  if (typeof v === 'number') return v;
  const s = String(v ?? '').trim();
  if (!s) return NaN;
  return Number(s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s);
}

/** Quantidade de uma peça: > 0, até 4 casas (a coluna é numeric(14,4)). null = inválida. */
export function lerQuantidadePeca(v: number | string | null | undefined): number | null {
  const n = numero(v);
  if (!Number.isFinite(n)) return null;
  const arredondada = Math.round(n * 10000) / 10000;
  return arredondada > 0 && arredondada < 1e10 ? arredondada : null;
}

function arredondarMicroParaCentavos(micro: bigint): number {
  const zero = BigInt(0);
  const negativo = micro < zero;
  const abs = negativo ? -micro : micro;
  const centavos = (abs + BigInt(5000)) / BigInt(10000);
  return Number(negativo ? -centavos : centavos);
}

function microDaPeca(p: PecaParaSomar): bigint {
  const q = lerQuantidadePeca(p.quantidade);
  const v = numero(p.valor_unitario);
  if (q === null || !Number.isFinite(v)) return BigInt(0);
  return BigInt(Math.round(q * 10000)) * BigInt(Math.round(v * 100));
}

/** Valor do composto em centavos inteiros, como o servidor vai gravar. */
export function totalCompostoCentavos(pecas: PecaParaSomar[]): number {
  const soma = pecas.reduce((acc, p) => acc + microDaPeca(p), BigInt(0));
  return arredondarMicroParaCentavos(soma);
}

/** Subtotal de UMA linha, só para exibição (o total não é a soma destes arredondados). */
export function subtotalPecaCentavos(p: PecaParaSomar): number {
  return arredondarMicroParaCentavos(microDaPeca(p));
}

export function reaisDeCentavos(centavos: number): string {
  return 'R$ ' + (centavos / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function semAcento(s: string | null | undefined): string {
  return String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

/**
 * A estação soma só compra e instalação (decisão de Lucas, 15/09/2026). Peça
 * cobrada por mês (plano satelital) não entra; peça por diária pode ser a diária
 * do técnico que instala — só pede conferência.
 */
export function avisoUnidadePeca(unidade: string | null | undefined): 'mensal' | 'diaria' | null {
  const u = semAcento(unidade);
  if (!u) return null;
  if (/(^|[^a-z])(mes|meses|mensal|mensalidade)([^a-z]|$)/.test(u)) return 'mensal';
  if (/(^|[^a-z])(diaria|diarias|dia|dias)([^a-z]|$)/.test(u)) return 'diaria';
  return null;
}

/** Ids que já têm `proprioId` em algum nível abaixo — escolher um deles criaria um ciclo. */
export function idsQueContem(catalogo: CustoDoCatalogo[], proprioId: string | null | undefined): Set<string> {
  const resultado = new Set<string>();
  if (!proprioId) return resultado;
  const filhos = new Map<string, string[]>();
  for (const c of catalogo) filhos.set(c.id, (c.componentes || []).map(p => p.componente_id));
  const contem = (id: string, visitados: Set<string>): boolean => {
    if (visitados.has(id)) return false;
    visitados.add(id);
    return (filhos.get(id) || []).some(f => f === proprioId || contem(f, visitados));
  };
  for (const c of catalogo) if (c.id !== proprioId && contem(c.id, new Set())) resultado.add(c.id);
  return resultado;
}

/** Custos que podem entrar como peça: ativos, nem o próprio, nem já escolhidos, nem quem o contém. */
export function pecasDisponiveis(
  catalogo: CustoDoCatalogo[], proprioId: string | null | undefined, escolhidos: string[],
): CustoDoCatalogo[] {
  const bloqueados = idsQueContem(catalogo, proprioId);
  const ja = new Set(escolhidos);
  return catalogo.filter(c => c.ativo !== false && c.id !== proprioId && !ja.has(c.id) && !bloqueados.has(c.id));
}

/** Busca sem acento em descrição, código, tag e categoria ("estacao" acha "Estação"). */
export function casaBuscaPeca(custo: CustoDoCatalogo, busca: string): boolean {
  const termos = semAcento(busca).split(/\s+/).filter(Boolean);
  if (!termos.length) return true;
  const alvo = semAcento([custo.descricao, custo.codigo, custo.tag, custo.categoria].filter(Boolean).join(' '));
  return termos.every(t => alvo.includes(t));
}

/** «A», «B» e «C» — com "e mais N" depois de três. */
export function juntarNomes(nomes: string[]): string {
  const citados = nomes.slice(0, 3).map(n => `«${n}»`);
  const resto = nomes.length > 3 ? ` e mais ${nomes.length - 3}` : '';
  if (citados.length <= 1 || resto) return citados.join(', ') + resto;
  return `${citados.slice(0, -1).join(', ')} e ${citados[citados.length - 1]}`;
}

/**
 * A lista de peças mudou em relação à que a tela carregou? Só então o PUT leva
 * `componentes`: uma aba aberta há horas que só corrige a observação não pode
 * regravar peças que outra pessoa trocou nesse meio-tempo.
 */
export function pecasMudaram(
  originais: { componente_id: string; quantidade: number | string }[],
  atuais: { componente_id: string; quantidade: number | string | null }[],
): boolean {
  if (originais.length !== atuais.length) return true;
  return originais.some((o, i) => o.componente_id !== atuais[i].componente_id
    || lerQuantidadePeca(o.quantidade) !== lerQuantidadePeca(atuais[i].quantidade));
}
